import { Dashboardv99990101Api } from '@sequencehq/api/dashboard/v99990101'
import { toBillingFrequencyLabel } from '@sequencehq/utils'
import { match } from 'ts-pattern'

type GrantRefreshFrequency =
  Dashboardv99990101Api.CreditGrants.GrantRefreshFrequency
type GrantBillingFrequency =
  Dashboardv99990101Api.CreditGrants.GrantBillingFrequency

export const GRANT_BILLING_FREQUENCIES = [
  'ONE_TIME',
  'MONTHLY',
  'QUARTERLY',
  'HALF_YEARLY',
  'YEARLY'
] as const satisfies readonly GrantBillingFrequency[]

// Keyed off both enums, so a new recurring cadence in either fails to compile
// until its length is stated here.
const CADENCE_MONTHS: Record<
  | Exclude<GrantBillingFrequency, 'ONE_TIME'>
  | Exclude<GrantRefreshFrequency, 'NONE'>,
  number
> = {
  MONTHLY: 1,
  QUARTERLY: 3,
  HALF_YEARLY: 6,
  YEARLY: 12
}

/** ONE_TIME and NONE are each enum's non-recurring member. */
const cadenceMonths = (
  cadence: GrantBillingFrequency | GrantRefreshFrequency
): number | null =>
  cadence === 'ONE_TIME' || cadence === 'NONE' ? null : CADENCE_MONTHS[cadence]

/**
 * One charge has to cover whole refresh periods, so it can be no finer than the
 * refresh. A grant that never refreshes takes a one-off charge and nothing else.
 */
const canFundRefresh = (
  billingFrequency: GrantBillingFrequency,
  grantRefreshFrequency: GrantRefreshFrequency
): boolean => {
  const charge = cadenceMonths(billingFrequency)
  const refresh = cadenceMonths(grantRefreshFrequency)

  if (charge === null || refresh === null) {
    return charge === refresh
  }

  return charge >= refresh
}

/**
 * How many refresh periods one charge pays for, matching the platform's
 * `refreshPeriodsCovered`. With no billing frequency the platform charges on
 * each refresh, so that's one period.
 *
 *   refresh MONTHLY, billing YEARLY  -> 12
 *   refresh NONE,    billing ONE_TIME -> 1
 */
export const refreshPeriodsPerCharge = (
  grantRefreshFrequency: GrantRefreshFrequency,
  billingFrequency: GrantBillingFrequency | undefined
): number => {
  const charge = billingFrequency ? cadenceMonths(billingFrequency) : null
  const refresh = cadenceMonths(grantRefreshFrequency)

  if (charge === null || refresh === null) {
    return 1
  }

  return charge / refresh
}

/** The platform's default when billingFrequency is omitted. */
const billedEachRefresh = (
  grantRefreshFrequency: GrantRefreshFrequency
): GrantBillingFrequency =>
  match(grantRefreshFrequency)
    .with('NONE', () => 'ONE_TIME' as const)
    .with('MONTHLY', () => 'MONTHLY' as const)
    .with('QUARTERLY', () => 'QUARTERLY' as const)
    .with('HALF_YEARLY', () => 'HALF_YEARLY' as const)
    .with('YEARLY', () => 'YEARLY' as const)
    .exhaustive()

/** Keeps `current` where it still funds the refresh cadence. */
export const resolveBillingFrequency = (
  current: GrantBillingFrequency | undefined,
  grantRefreshFrequency: GrantRefreshFrequency
): GrantBillingFrequency =>
  current && canFundRefresh(current, grantRefreshFrequency)
    ? current
    : billedEachRefresh(grantRefreshFrequency)

export const billingFrequencyOptionsFor = (
  grantRefreshFrequency: GrantRefreshFrequency
): Array<{ label: string; value: GrantBillingFrequency }> =>
  GRANT_BILLING_FREQUENCIES.filter(billingFrequency =>
    canFundRefresh(billingFrequency, grantRefreshFrequency)
  ).map(value => ({ label: toBillingFrequencyLabel(value), value }))
